import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Rocket,
    GitCommit,
    Clock,
    ChevronDown,
    ChevronUp,
    Loader2,
    RefreshCw,
    ExternalLink,
    CheckCircle,
    XCircle,
    AlertCircle,
    Globe
} from 'lucide-react';
import { useInfrastructure } from '../../hooks/useInfrastructure';
import { useDeployments } from '../../hooks/useDeployments';
import { useWorkspaceCommits } from '../../hooks/useWorkspaceCommits';
import { useAuth } from '../../contexts/AuthContext';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';

interface PublishSectionProps {
    workspace: any;
    workspaceId: string;
    api: any;
}

// Status constants to avoid magic strings
const STATUS = {
    READY: 'ready',
    COMPLETE: 'complete',
    CREATING: 'creating',
    RUNNING: 'running',
    FAILED: 'failed',
    NOT_STARTED: 'not_started'
} as const;

const DEPLOYMENT_STATUS = {
    COMPLETE: 'COMPLETE',
    RUNNING: 'RUNNING',
    FAILED: 'FAILED'
} as const;

const PublishSection: React.FC<PublishSectionProps> = ({ workspace, workspaceId, api }) => {
    const { isAdmin } = useAuth();
    const { isReadOnly } = useReadOnlyMode();
    const [deployModalOpen, setDeployModalOpen] = useState(false);
    const [selectedCommit, setSelectedCommit] = useState<string>('');
    const [deployError, setDeployError] = useState<string | null>(null);
    const [expandedDeployments, setExpandedDeployments] = useState<Set<string>>(new Set());
    const [isRefreshing, setIsRefreshing] = useState<{ infrastructure: boolean; deployments: boolean }>({
        infrastructure: false,
        deployments: false
    });
    const [loadingSteps, setLoadingSteps] = useState<Set<string>>(new Set());

    const {
        infrastructure,
        loading: infrastructureLoading,
        error: infrastructureError,
        isAllReady,
        refresh: refreshInfrastructure
    } = useInfrastructure(workspaceId);

    const {
        deployments,
        deploymentSteps,
        loading: deploymentsLoading,
        error: deploymentsError,
        creatingDeployment,
        fetchDeployments,
        createDeployment,
        fetchDeploymentSteps
    } = useDeployments(workspaceId);

    const {
        commits,
        loading: commitsLoading,
        error: commitsError,
        refetch: refetchCommits
    } = useWorkspaceCommits(workspaceId, 10);

    // Memoized sorted deployments
    const sortedDeployments = useMemo(() => {
        return [...deployments].sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }, [deployments]);

    // Check if workspace has a successful deployment
    const hasSuccessfulDeployment = useMemo(() => {
        return deployments.some(d => d.status === DEPLOYMENT_STATUS.COMPLETE);
    }, [deployments]);

    // Handle escape key for modal
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && deployModalOpen) {
                handleCloseModal();
            }
        };

        if (deployModalOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [deployModalOpen]);

    const handleRefreshInfrastructure = useCallback(async () => {
        setIsRefreshing(prev => ({ ...prev, infrastructure: true }));
        try {
            await refreshInfrastructure();
        } finally {
            setIsRefreshing(prev => ({ ...prev, infrastructure: false }));
        }
    }, [refreshInfrastructure]);

    const handleRefreshDeployments = useCallback(async () => {
        setIsRefreshing(prev => ({ ...prev, deployments: true }));
        try {
            await fetchDeployments();

            if (expandedDeployments.size > 0) {
                const stepRefreshPromises = Array.from(expandedDeployments).map(async (deploymentId) => {
                    try {
                        await fetchDeploymentSteps(deploymentId);
                    } catch (err) {
                        console.error(`Failed to refresh steps for deployment ${deploymentId}:`, err);
                    }
                });

                await Promise.all(stepRefreshPromises);
            }
        } finally {
            setIsRefreshing(prev => ({ ...prev, deployments: false }));
        }
    }, [fetchDeployments, fetchDeploymentSteps, expandedDeployments]);

    const handleOpenDeployModal = useCallback(() => {
        setDeployModalOpen(true);
        setSelectedCommit('');
        setDeployError(null);
        refetchCommits();
    }, [refetchCommits]);

    const handleCloseModal = useCallback(() => {
        setDeployModalOpen(false);
        setSelectedCommit('');
        setDeployError(null);
    }, []);

    const handleCreateDeployment = useCallback(async () => {
        if (!selectedCommit) {
            setDeployError('Please select a commit to deploy');
            return;
        }

        try {
            setDeployError(null);
            await createDeployment(selectedCommit);
            handleCloseModal();
            // Optionally show success notification
        } catch (err: any) {
            setDeployError(err.message || 'Failed to create deployment');
        }
    }, [selectedCommit, createDeployment, handleCloseModal]);

    const toggleDeploymentExpansion = useCallback(async (deploymentId: string) => {
        const newExpanded = new Set(expandedDeployments);

        if (newExpanded.has(deploymentId)) {
            newExpanded.delete(deploymentId);
        } else {
            newExpanded.add(deploymentId);

            // Fetch steps if not already loaded
            if (!deploymentSteps[deploymentId] && !loadingSteps.has(deploymentId)) {
                setLoadingSteps(prev => {
                    const next = new Set(Array.from(prev));
                    next.add(deploymentId);
                    return next;
                });
                try {
                    await fetchDeploymentSteps(deploymentId);
                } catch (err) {
                    console.error('Failed to fetch deployment steps:', err);
                    // Optionally show error notification
                } finally {
                    setLoadingSteps(prev => {
                        const next = new Set(Array.from(prev));
                        next.delete(deploymentId);
                        return next;
                    });
                }
            }
        }
        setExpandedDeployments(newExpanded);
    }, [expandedDeployments, deploymentSteps, fetchDeploymentSteps, loadingSteps]);

    const getStatusColor = useCallback((status: string) => {
        const normalizedStatus = status.toLowerCase();
        switch (normalizedStatus) {
            case STATUS.READY:
            case STATUS.COMPLETE:
                return 'bg-lime-500/20 text-lime-700 dark:text-lime-400 border border-lime-500/30';
            case STATUS.CREATING:
            case STATUS.RUNNING:
                return 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30';
            case STATUS.FAILED:
                return 'bg-red-600/10 text-red-600 dark:text-red-400 border border-red-600/20 dark:border-red-500/30';
            case STATUS.NOT_STARTED:
            default:
                return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-300 dark:border-neutral-700';
        }
    }, []);

    const getStatusIcon = useCallback((status: string) => {
        const normalizedStatus = status.toLowerCase();
        switch (normalizedStatus) {
            case STATUS.READY:
            case STATUS.COMPLETE:
                return <CheckCircle className="w-4 h-4" />;
            case STATUS.CREATING:
            case STATUS.RUNNING:
                return <Loader2 className="w-4 h-4 animate-spin" />;
            case STATUS.FAILED:
                return <XCircle className="w-4 h-4" />;
            case STATUS.NOT_STARTED:
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    }, []);

    const formatDate = useCallback((dateString: string) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return 'Invalid date';
        }
    }, []);

    const formatCommitDate = useCallback((dateString: string) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();

            if (diffMs < 0) {
                return 'Just now'; // Handle future dates
            }

            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffMinutes < 60) {
                return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
            } else if (diffHours < 24) {
                return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            } else if (diffDays < 7) {
                return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            } else {
                return date.toLocaleDateString();
            }
        } catch {
            return 'Invalid date';
        }
    }, []);

    // Auto-select the first commit when modal opens and commits are loaded
    useEffect(() => {
        if (deployModalOpen && commits && commits.length > 0 && !selectedCommit) {
            setSelectedCommit(commits[0].id);
        }
    }, [deployModalOpen, commits, selectedCommit]);

    // Handle backdrop click
    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    }, [handleCloseModal]);

    // Show loading state
    if (infrastructureLoading && deploymentsLoading && !infrastructure.length && !deployments.length) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
        );
    }

    return (
        <div>
            {/* Infrastructure Section */}
            <div style={{ display: 'none' }}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Infrastructure</h2>
                    <button
                        onClick={handleRefreshInfrastructure}
                        disabled={isRefreshing.infrastructure}
                        className="p-2 hover:bg-neutral-200 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh infrastructure status"
                        data-tour="refresh-infrastructure"
                    >
                        <RefreshCw className={`w-4 h-4 text-neutral-600 dark:text-neutral-400 ${isRefreshing.infrastructure ? 'animate-spin' : ''
                            }`} />
                    </button>
                </div>

                {/* Infrastructure Elements */}
                {infrastructureError ? (
                    <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl">
                        <p className="text-red-700 dark:text-red-400">{infrastructureError}</p>
                    </div>
                ) : infrastructure.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {infrastructure.map((item) => (
                            <div key={item.id} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                                            {item.element_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                        </h3>
                                        <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${item.status.toLowerCase() === STATUS.READY
                                            ? 'bg-lime-500/20 text-lime-700 dark:text-lime-400 border border-lime-500/30'
                                            : item.status.toLowerCase() === STATUS.CREATING
                                                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30'
                                                : item.status.toLowerCase() === STATUS.FAILED
                                                    ? 'bg-red-600/10 text-red-600 dark:text-red-400 border border-red-600/20 dark:border-red-500/30'
                                                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-300 dark:border-neutral-700'
                                            }`}>
                                            {getStatusIcon(item.status)}
                                            <span className="ml-1">{item.status.replace(/_/g, ' ')}</span>
                                        </span>
                                    </div>

                                    <div className="space-y-2 text-sm">
                                        <div>
                                            <p className="text-neutral-600 dark:text-neutral-400">Created</p>
                                            <p className="text-neutral-900 dark:text-neutral-200">{formatDate(item.created_at)}</p>
                                        </div>

                                        {item.updated_at !== item.created_at && (
                                            <div>
                                                <p className="text-neutral-600 dark:text-neutral-400">Last Updated</p>
                                                <p className="text-neutral-900 dark:text-neutral-200">{formatDate(item.updated_at)}</p>
                                            </div>
                                        )}

                                        {isAdmin && item.gitlab_pipeline_url && (
                                            <div>
                                                <p className="text-neutral-600 dark:text-neutral-400">Pipeline</p>
                                                <a
                                                    href={item.gitlab_pipeline_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center"
                                                >
                                                    View Pipeline
                                                    <ExternalLink className="w-3 h-3 ml-1" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : infrastructureLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-neutral-600 dark:text-neutral-400">No infrastructure elements found for this workspace.</p>
                    </div>
                )}
            </div>

            {/* Deployments Section */}
            <div>
                <div className="flex justify-between items-center mb-4">
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Deployments</h2>
                        {/* Show deployed URL if available and there's a successful deployment */}
                        {workspace?.deployed_url && hasSuccessfulDeployment && (
                            <div className="flex items-center mt-2">
                                <Globe className="w-4 h-4 text-neutral-500 dark:text-neutral-400 mr-2" />
                                <a
                                    href={workspace.deployed_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center"
                                >
                                    {workspace.deployed_url}
                                    <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleRefreshDeployments}
                            disabled={isRefreshing.deployments}
                            className="p-2 hover:bg-neutral-200 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50"
                            title="Refresh deployments"
                            data-tour="refresh-deployments"
                        >
                            <RefreshCw className={`w-4 h-4 text-neutral-600 dark:text-neutral-400 ${isRefreshing.deployments ? 'animate-spin' : ''
                                }`} />
                        </button>
                        <button
                            onClick={handleOpenDeployModal}
                            disabled={isReadOnly || !isAllReady || creatingDeployment}
                            className={`inline-flex items-center px-4 py-2 rounded-xl font-medium transition-all ${isAllReady && !creatingDeployment
                                ? (isReadOnly ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed' : 'bg-brand-action hover:bg-brand-action-hover text-brand-action-text shadow-sm')
                                : 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                                }`}
                            data-tour="deploy-project"
                        >
                            <Rocket className="w-4 h-4 mr-2" />
                            Publish
                        </button>
                    </div>
                </div>

                {!isAllReady && infrastructure.length > 0 && (
                    <div className="mb-4 p-4 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-xl">
                        <p className="text-sm text-yellow-700 dark:text-yellow-400">
                            All infrastructure must be in "Ready" status before you can create a deployment.
                        </p>
                    </div>
                )}

                {deploymentsError ? (
                    <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl">
                        <p className="text-red-700 dark:text-red-400">{deploymentsError}</p>
                    </div>
                ) : sortedDeployments.length > 0 ? (
                    <div className="space-y-4">
                        {sortedDeployments.map((deployment) => (
                            <div key={deployment.id} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                                <div className="p-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center mb-2">
                                                <GitCommit className="w-4 h-4 text-neutral-500 dark:text-neutral-400 mr-2" />
                                                <code className="text-sm font-mono text-neutral-900 dark:text-neutral-100">
                                                    {deployment.git_commit_hash}
                                                </code>
                                            </div>
                                            <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                                                <Clock className="w-3 h-3 mr-1" />
                                                {formatDate(deployment.created_at)}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${deployment.status === DEPLOYMENT_STATUS.COMPLETE
                                                ? 'bg-lime-500/20 text-lime-700 dark:text-lime-400 border border-lime-500/30'
                                                : deployment.status === DEPLOYMENT_STATUS.RUNNING
                                                    ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30'
                                                    : deployment.status === DEPLOYMENT_STATUS.FAILED
                                                        ? 'bg-red-600/10 text-red-600 dark:text-red-400 border border-red-600/20 dark:border-red-500/30'
                                                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-300 dark:border-neutral-700'
                                                }`}>
                                                {getStatusIcon(deployment.status)}
                                                <span className="ml-1">{deployment.status.replace(/_/g, ' ')}</span>
                                            </span>
                                            <button
                                                onClick={() => toggleDeploymentExpansion(deployment.id)}
                                                disabled={loadingSteps.has(deployment.id)}
                                                className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors disabled:opacity-50"
                                                title="Show deployment steps"
                                            >
                                                {expandedDeployments.has(deployment.id) ? (
                                                    <ChevronUp className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {expandedDeployments.has(deployment.id) && (
                                    <div className="border-t border-neutral-200 dark:border-neutral-700 p-4 bg-neutral-50 dark:bg-neutral-900/50">
                                        <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Deployment Steps</h4>
                                        {loadingSteps.has(deployment.id) || (deploymentSteps[deployment.id] === undefined && expandedDeployments.has(deployment.id)) ? (
                                            <div className="flex justify-center items-center py-4">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400"></div>
                                            </div>
                                        ) : deploymentSteps[deployment.id] && deploymentSteps[deployment.id].length > 0 ? (
                                            <div className="space-y-2">
                                                {deploymentSteps[deployment.id].map((step) => (
                                                    <div key={step.id} className="flex items-center justify-between p-3 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                                        <div className="flex items-center">
                                                            {getStatusIcon(step.status)}
                                                            <span className="ml-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                                                {step.infrastructure_element_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center space-x-3">
                                                            {isAdmin && step.gitlab_pipeline_url && (
                                                                <a
                                                                    href={step.gitlab_pipeline_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center"
                                                                >
                                                                    View Pipeline
                                                                    <ExternalLink className="w-3 h-3 ml-1" />
                                                                </a>
                                                            )}
                                                            <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${getStatusColor(step.status)}`}>
                                                                {step.status.replace(/_/g, ' ')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">No deployment steps found.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : deploymentsLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-neutral-600 dark:text-neutral-400">No deployments found for this workspace.</p>
                    </div>
                )}
            </div>

            {/* Deploy Modal */}
            {deployModalOpen && (
                <div
                    className="fixed inset-0 bg-black/30 dark:bg-black/60 flex items-center justify-center z-50"
                    onClick={handleBackdropClick}
                >
                    <div
                        className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Create Deployment</h2>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Select a commit to deploy
                                </label>

                                {commitsLoading ? (
                                    <div className="flex justify-center items-center py-4">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400"></div>
                                    </div>
                                ) : commitsError ? (
                                    <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-sm">
                                        <p className="text-red-700 dark:text-red-400">{commitsError.message || 'Failed to load commits'}</p>
                                    </div>
                                ) : commits && commits.length > 0 ? (
                                    <div className="border border-neutral-300 dark:border-neutral-600 rounded-lg max-h-60 overflow-y-auto">
                                        {commits.map((commit) => (
                                            <div
                                                key={commit.id}
                                                onClick={() => setSelectedCommit(commit.id)}
                                                className={`p-3 border-b border-neutral-300 dark:border-neutral-600 last:border-b-0 cursor-pointer transition-colors ${selectedCommit === commit.id
                                                    ? 'bg-blue-100 dark:bg-blue-900/30'
                                                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                                    }`}
                                            >
                                                <div className="flex items-center mb-1">
                                                    <GitCommit className="w-3 h-3 text-neutral-500 dark:text-neutral-400 mr-2" />
                                                    <code className="text-xs font-mono text-neutral-900 dark:text-neutral-100">
                                                        {commit.id}
                                                    </code>
                                                </div>
                                                <p className="text-sm text-neutral-700 dark:text-neutral-300 truncate">{commit.message}</p>
                                                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                                    {formatCommitDate(commit.date)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-center">
                                        <p className="text-neutral-600 dark:text-neutral-400">No commits found for this workspace.</p>
                                    </div>
                                )}
                            </div>

                            {deployError && (
                                <div className="p-3 mb-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
                                    <p className="text-sm text-red-700 dark:text-red-400">{deployError}</p>
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={creatingDeployment}
                                    className="px-4 py-2 text-sm rounded-lg bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateDeployment}
                                    disabled={!selectedCommit || creatingDeployment}
                                    className={`px-4 py-2 text-sm rounded-lg flex items-center transition-all ${!selectedCommit || creatingDeployment
                                        ? 'bg-brand-action/50 text-brand-action-text/70 cursor-not-allowed'
                                        : 'bg-brand-action hover:bg-brand-action-hover text-brand-action-text'
                                        }`}
                                >
                                    {creatingDeployment && <Loader2 className="w-3 h-3 animate-spin mr-2" />}
                                    Create Deployment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublishSection;